/**
 * Publish the draft (T3.9), with backdating (T7.27).
 *
 * Publishing is explicit and separate from editing: clicking chips writes the
 * draft, and only this control makes it what viewers see. The API does the work
 * in one transaction (design.md § 6); this component is the control, the
 * confirmation and the release-note field, which the API calls `label`.
 *
 * The wording is deliberate. Versions read as Draft and Live, never
 * "Published", because Published is also a deliverable state (design.md § 2).
 * The noun for the blueprint itself comes from useTerms() like every other word.
 *
 * It is disabled while writes are still in flight. Publishing ahead of a pending
 * state change would snapshot a draft that is about to change, so the button
 * waits and says why.
 *
 * It is not rendered at all for a viewer — the caller omits it — and the API
 * refuses the call regardless.
 *
 * **Backdating (T7.27, design.md § 10a, 7.4).** An optional week this version
 * should count AS. Three rules the interface has to get right:
 *
 *   1. Only weeks that have already ended are offered. Backdating sets
 *      `effective_at` to the END of the chosen week, so "this week" is in the
 *      future and would outrank a later ordinary publish. The API refuses the
 *      current week and every future one; this list never offers them, so the
 *      refusal is not something a user has to discover.
 *   2. Publishing without it is what "this week" means. It is therefore off by
 *      default and unticking it is the ordinary path.
 *   3. `week_occupied` (409) is NOT a failure — it is a question. The week
 *      already holds a BACKDATED version, so the dialogue names that version and
 *      offers to supersede it; `supersede: true` is this component coming back
 *      having asked. **Ordinary publishing never raises it** — publishing twice
 *      in a week is normal and supersedes nothing — so no confirmation step is
 *      put in front of an ordinary publish.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api';
import { blueprintKeys } from '../../lib/blueprints';
import { useWeekSeries } from '../../lib/weekSeries';
import { useTerms } from '../../lib/terms';
import { dayFmt, mondayOf, ymd } from '../blueprint/weeks';
import { Dialog } from './Dialog';

/** Mirrors the API's limit on the release note. */
export const LABEL_MAX = 200;

/** How many recent weeks to offer when the plan's own weeks do not reach back. */
const RECENT_WEEKS = 8;

interface WeekOption {
  /** Monday, `YYYY-MM-DD` — exactly what the API accepts, which checks the calendar. */
  start: string;
  label: string;
  /** The version whose figure that week currently shows, when there is one. */
  versionNo: number | null;
}

/** The version the API found in the week, as it sends it in `details`. */
interface Occupied {
  weekStart: string;
  versionNo: number;
  label: string | null;
}

function occupiedFrom(err: unknown): Occupied | null {
  if (!(err instanceof ApiError) || err.knownCode !== 'week_occupied') return null;
  const details = err.body?.error?.details as
    | { weekStart?: unknown; existing?: { versionNo?: unknown; label?: unknown } }
    | undefined;
  const weekStart = details?.weekStart;
  const versionNo = details?.existing?.versionNo;
  if (typeof weekStart !== 'string' || typeof versionNo !== 'number') return null;
  const label = details?.existing?.label;
  return { weekStart, versionNo, label: typeof label === 'string' ? label : null };
}

/** "14 Sept – 18 Sept 2026". Never lower-cased: it carries month names. */
function weekRange(monday: string): string {
  const friday = new Date(monday + 'T00:00:00');
  friday.setDate(friday.getDate() + 4);
  return `${dayFmt(monday)} – ${dayFmt(ymd(friday))} ${friday.getFullYear()}`;
}

function weekLabel(monday: string): string {
  return `Week of ${weekRange(monday)}`;
}

/**
 * The weeks that may be backdated into: every week of the plan that has ended,
 * plus the last few weeks, most recent first.
 *
 * Both sources are needed. The plan's weeks are what a report is actually filed
 * against, but a blueprint with no Schedule sheet has none at all, and a plan
 * that ended months ago offers nothing recent — so the recent weeks are added
 * and the two are merged rather than chosen between.
 */
function backdateOptions(
  series: { start: string; versionNo: number | null; carriedForward: boolean }[],
  today = new Date(),
): WeekOption[] {
  const thisMonday = ymd(mondayOf(today));
  const versionByWeek = new Map<string, number>();
  const starts = new Set<string>();

  for (const row of series) {
    if (row.start >= thisMonday) continue;
    starts.add(row.start);
    // A carried-forward week has no version of its own; naming the carried one
    // would suggest a version sits there that does not.
    if (row.versionNo !== null && !row.carriedForward) versionByWeek.set(row.start, row.versionNo);
  }

  for (let i = 1; i <= RECENT_WEEKS; i++) {
    const d = mondayOf(today);
    d.setDate(d.getDate() - 7 * i);
    starts.add(ymd(d));
  }

  return [...starts]
    .sort((a, b) => (a < b ? 1 : -1))
    .map((start) => ({
      start,
      label: weekLabel(start),
      versionNo: versionByWeek.get(start) ?? null,
    }));
}

export function PublishButton({
  blueprintId,
  versionNo,
  pendingWrites,
  onPublished,
}: {
  blueprintId: string;
  versionNo: number;
  pendingWrites: number;
  /** Called with the new version number once the API has confirmed. */
  onPublished: (publishedVersionNo: number) => void;
}) {
  const terms = useTerms();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Backdating is off until asked for: an ordinary publish is "this week".
  const [backdating, setBackdating] = useState(false);
  const [week, setWeek] = useState('');
  const [occupied, setOccupied] = useState<Occupied | null>(null);

  // Only fetched while the dialogue is open: it is a per-version walk on the API
  // and nothing on this screen needs it until a week has to be chosen.
  const series = useWeekSeries(blueprintId, open);
  const options = useMemo(() => backdateOptions(series.data?.weeks ?? []), [series.data]);

  const blocked = pendingWrites > 0;

  const close = () => {
    if (busy) return;
    setOpen(false);
    setError(null);
    setOccupied(null);
  };

  /** Any change to what is being published invalidates an answer already given. */
  const changeWeek = (next: string) => {
    setWeek(next);
    setOccupied(null);
    setError(null);
  };
  const toggleBackdating = (on: boolean) => {
    setBackdating(on);
    setOccupied(null);
    setError(null);
    if (on && !week && options[0]) setWeek(options[0].start);
  };

  const run = async (supersede: boolean) => {
    if (busy || blocked) return;
    setBusy(true);
    setError(null);
    try {
      const trimmed = label.trim();
      const body: Record<string, unknown> = {};
      if (trimmed) body.label = trimmed;
      // Absent, not null: absent is the ordinary publish, and the API reads a
      // missing key as "this week" rather than as a backdate to nowhere.
      if (backdating && week) body.backdateWeek = week;
      if (supersede) body.supersede = true;

      await api.post(`/blueprints/${blueprintId}/publish`, body);
      setOpen(false);
      setLabel('');
      setBackdating(false);
      setWeek('');
      setOccupied(null);
      // Everything about this blueprint has changed: the version list, the
      // draft (now a fresh clone), the latest published version and the weekly
      // series, which is keyed under the blueprint's detail key.
      await queryClient.invalidateQueries({ queryKey: blueprintKeys.all });
      onPublished(versionNo);
    } catch (err) {
      const taken = occupiedFrom(err);
      if (taken) {
        // A question rather than a refusal: the dialogue names what is there and
        // offers to supersede it.
        setOccupied(taken);
      } else {
        setError(err instanceof ApiError ? err.message : 'Publishing failed. Nothing was changed.');
      }
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(false);
  };

  return (
    <>
      <button
        type="button"
        className="btn primary"
        onClick={() => setOpen(true)}
        disabled={blocked}
        title={blocked ? 'Waiting for your latest changes to finish saving' : undefined}
      >
        Publish
      </button>

      <Dialog open={open} title={`Publish version ${versionNo}?`} onClose={close} dismissible={!busy}>
        <form onSubmit={submit} className="bp-dialog-form">
          <p>
            This makes version {versionNo} the Live version. Everyone with access to this{' '}
            {terms.noun('blueprint')} will see it, and it can no longer be changed. A new draft
            is started from it so editing can carry on.
          </p>

          <label className="field">
            <span className="field-label">Release note (optional)</span>
            <input
              type="text"
              value={label}
              maxLength={LABEL_MAX}
              onChange={(e) => setLabel(e.target.value)}
              disabled={busy}
              autoFocus
            />
          </label>

          <div className="field">
            <label className="bp-check">
              <input
                type="checkbox"
                checked={backdating}
                onChange={(e) => toggleBackdating(e.target.checked)}
                disabled={busy || !options.length}
              />
              <span>This version counts as an earlier week</span>
            </label>
            <p className="bp-dropzone-hint">
              For a workbook that describes an earlier week — one you are only uploading now. The
              weekly progress slider will show it against the week you pick, not against today.
              Leave it unticked and this version counts as the current week, which is the usual
              case. Only weeks that have already ended can be chosen: a version counts from the
              end of its week, so this week is still ahead.
            </p>

            {backdating ? (
              <>
                {/* Unclassed: `.field select` in shell.css already styles a
                    select inside a .field, in both themes. */}
                <span className="field-label">Which week does this version count as?</span>
                <select
                  value={week}
                  onChange={(e) => changeWeek(e.target.value)}
                  disabled={busy}
                  aria-label="Which week does this version count as?"
                >
                  {options.map((o) => (
                    <option value={o.start} key={o.start}>
                      {o.label}
                      {o.versionNo !== null ? ` — currently version ${o.versionNo}` : ''}
                    </option>
                  ))}
                </select>
                <p className="bp-dropzone-hint">
                  When it was really published is recorded either way; this changes only the week
                  it counts as.
                </p>
              </>
            ) : null}
          </div>

          {occupied ? (
            <div className="warnbar show" role="alert">
              <b>
                Version {occupied.versionNo}
                {occupied.label ? ` — ${occupied.label}` : ''} is already the backdated version for
                the week of {weekRange(occupied.weekStart)}.
              </b>{' '}
              Publishing into that week replaces it: it is hidden from the version list and from
              the weekly progress slider. Its content is kept and can be recovered — nothing is
              deleted.
            </div>
          ) : null}

          {error ? (
            <p className="bp-inline-err" role="alert">
              {error}
            </p>
          ) : null}

          <div className="bp-dialog-actions">
            <button type="button" className="btn" onClick={close} disabled={busy}>
              Cancel
            </button>
            {occupied ? (
              <button
                type="button"
                className="btn primary"
                onClick={() => void run(true)}
                disabled={busy || blocked}
              >
                {busy ? 'Publishing…' : `Replace version ${occupied.versionNo} and publish`}
              </button>
            ) : (
              <button type="submit" className="btn primary" disabled={busy || blocked}>
                {busy ? 'Publishing…' : 'Publish'}
              </button>
            )}
          </div>
        </form>
      </Dialog>
    </>
  );
}
